import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Pagination,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import CustomTable from "../../../components/CustomTable";
import { getCronJobsList } from "../../../services/cronJobService";
import { formatDate } from "../../../libs/utils";
import Swal from "sweetalert2";
import CronJobScheduleModal from "./CronJobScheduleModal";
import { useTimezone } from "../../../contexts/TimezoneContext";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";

function CronJobsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedJobName, setSelectedJobName] = useState("");
  const [selectedJobState, setSelectedJobState] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [searchKey, setSearchKey] = useState("");
  const [allRows, setAllRows] = useState([]);

  const { timezoneValue } = useTimezone();

  const columns = [
    {
      id: "job_name",
      label: "Job Name",
      minWidth: 150,
      enableSort: true,
      align: "left",
    },
    {
      id: "prev_fire_time",
      label: "Previous Fire Time",
      minWidth: 180,
      align: "left",
      enableSort: true,
      format: (value) => (value ? formatDate(value, timezoneValue) : "N/A"),
    },
    {
      id: "next_fire_time",
      label: "Next Fire Time",
      minWidth: 180,
      align: "left",
      enableSort: true,
      format: (value, row) => {
        const displayDate = value ? formatDate(value, timezoneValue) : "N/A";
        const futureFireTimes = row?.futureFireTime || [];

        if (Array.isArray(futureFireTimes) && futureFireTimes.length > 1) {
          const formattedDates = futureFireTimes.map((date) =>
            formatDate(date, timezoneValue)
          );

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span>{displayDate}</span>
              <Tooltip
                title={
                  <Box component="div" sx={{ p: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      Future Fire Times:
                    </Typography>
                    {formattedDates.map((date, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        component="div"
                        sx={{ mb: 0.25 }}
                      >
                        • {date}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton size="large" sx={{ padding: "4px" }}>
                  <InfoIcon fontSize="small" sx={{ color: "#1976d2" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return displayDate;
      },
    },
    {
      id: "current_state",
      label: "Current State",
      minWidth: 120,
      align: "left",
      format: (value) => {
        const stateColors = {
          NORMAL: { color: "#2e7d32", fontWeight: "bold" },
          BLOCKED: { color: "#f44336", fontWeight: "bold" },
          COMPLETED: { color: "#666", fontWeight: "bold" },
          ERROR: { color: "#f44336", fontWeight: "bold" },
        };
        const style = stateColors[value] || { color: "#666" };
        return <span style={style}>{value || "N/A"}</span>;
      },
    },
    {
      id: "action",
      label: "Action",
      minWidth: 120,
      align: "center",
    },
  ];

  const dataGenerator = (data) => {
    return {
      job_name: data.jobName || data.job_name,
      prev_fire_time: data.previousFireTime || data.prev_fire_time,
      next_fire_time: data.nextFireTime || data.next_fire_time,
      // Handle both camelCase and snake_case from API
      futureFireTime: data.futureFireTime || [],
      current_state: data.triggerState || data.current_state,
      action: (
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() =>
            handleActionClick(
              data.jobName || data.job_name,
              data.triggerState || data.current_state
            )
          }
        >
          Action
        </Button>
      ),
    };
  };

  const handleActionClick = (jobName, currentState) => {
    setSelectedJobName(jobName);
    setSelectedJobState(currentState);
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setSelectedJobName("");
    setSelectedJobState("");
  };

  const fetchJobsList = (page = currentPage) => {
    setLoading(true);
    getCronJobsList(page, pageSize)
      .then((response) => {
        const {
          success,
          error,
          jobs,
          currentPage: pageNum,
          totalPages: total,
          totalJobs: totalCount,
          pageSize: size,
        } = response || {};
        setLoading(false);
        if (success) {
          // Update pagination state
          setCurrentPage(pageNum !== undefined ? pageNum : page);
          setTotalPages(total || 0);
          setTotalJobs(totalCount || 0);
          if (size) setPageSize(size);

          if (jobs?.length && typeof jobs === "object") {
            const generatedRows = jobs.map((item) => dataGenerator(item));
            setAllRows(generatedRows);
            // Apply search filter if search key exists
            if (searchKey) {
              const filtered = generatedRows.filter((row) =>
                row.job_name?.toLowerCase().includes(searchKey.toLowerCase())
              );
              setRows(filtered);
            } else {
              setRows(generatedRows);
            }
          } else {
            setRows([]);
            setAllRows([]);
          }
        } else {
          Swal.fire({
            title: "Oops",
            text: error?.message || "Something went wrong",
            icon: "error",
            confirmButtonText: "Ok",
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: "Oops",
          text: error?.data?.error?.message || "Failed to fetch cron jobs",
          icon: "error",
          confirmButtonText: "Ok",
        });
      });
  };

  const handlePageChange = (event, newPage) => {
    // Material-UI Pagination is 1-indexed, backend is 0-indexed
    const pageIndex = newPage - 1;
    setCurrentPage(pageIndex);
    fetchJobsList(pageIndex);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchKey(searchValue);
    // Filter the current page's rows based on job name
    if (searchValue) {
      const filtered = allRows.filter((row) =>
        row.job_name?.toLowerCase().includes(searchValue.toLowerCase())
      );
      setRows(filtered);
    } else {
      setRows(allRows);
    }
  };

  useEffect(() => {
    fetchJobsList(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force re-render when timezone changes to update formatted dates
  useEffect(() => {
    // Trigger a re-render by updating rows state when timezone changes
    // This ensures the format functions in columns use the new timezoneValue
    if (allRows.length > 0) {
      setRows([...allRows]);
    }
  }, [timezoneValue]);

  return (
    <Box sx={{ px: 6 }} className="main-box">
      <div className="bankAddRuleHeader mb-3">
        <div
          style={{
            display: "flex",
            padding: "1.5rem 0",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#33343a" }} variant="h4" component="h4">
            Cron Jobs
          </Typography>
          {loading && (
            <div
              style={{
                marginLeft: "12px",
                color: "#a0a0a0",
                fontSize: "24px",
              }}
            >
              <i
                className={`fa fa-refresh ${loading === true ? "fa-spin" : ""}`}
                aria-hidden="true"
              ></i>
            </div>
          )}
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={() => fetchJobsList(currentPage)}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
      <div style={{ display: "flex", alignItems: "end", marginBottom: "16px" }}>
        <TextField
          id="standard-basic"
          label="Search by Job Name"
          variant="standard"
          sx={{ mr: 1, minWidth: "300px" }}
          value={searchKey}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: <SearchIcon sx={{ color: "#8a8a8a" }} />,
          }}
        />
      </div>
      <CustomTable
        data={rows}
        columns={columns}
        isPaginationRequired={false}
        defaultRowPerPage={pageSize}
      />
      {totalPages > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            mb: 2,
            px: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing page {currentPage + 1} of {totalPages} ({totalJobs} total
            jobs)
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Box>
      )}

      {modalOpened && (
        <CronJobScheduleModal
          jobName={selectedJobName}
          currentState={selectedJobState}
          getJobsList={() => fetchJobsList(currentPage)}
          handleClose={handleCloseModal}
        />
      )}
    </Box>
  );
}

export default CronJobsList;
